/**
 * The four chart primitives the analytics dashboard is built from (`SC-F808`).
 *
 * They live here rather than in `components/` for the reason `04` §6.1 states:
 * "hand-rolled inline SVG, no charting dependency, `role="img"` with a computed
 * text summary, and a keyboard-reachable `<details>` data table under every chart.
 * That is an accessibility requirement, not a style preference." The precedent is
 * bino-cloud's `pages/usage-chart.ts`, which is likewise a page-local element and
 * not part of the design-system kit — a chart is composed *from* the tokens, it is
 * not a token consumer the rest of the product reuses.
 *
 * # The colour rules these obey
 *
 * Every fill and stroke is a `--series-*` custom property, declared on each
 * element's own `:host` as an alias of a `--bino-*` semantic token. Declaring them
 * here rather than only on the page is deliberate and was learned the hard way: a
 * chart whose palette is only inherited renders **black** the first time it is
 * mounted anywhere else. Because they resolve through `--bino-*`, the charts follow
 * the theme with no `prefers-color-scheme` query anywhere in this directory, and
 * there is not one literal colour in the file.
 *
 * The palette is deliberately **small**, and that is a design decision rather than
 * a limitation of the token set:
 *
 *   - Everything that encodes *magnitude only* — query volume, top tables, top
 *     subjects, error-code ranks — takes the **one** neutral hue. Colouring
 *     nominal bars by their own value would spend the identity channel
 *     re-encoding what bar length already shows.
 *   - A series takes a **status** tone only where it genuinely means good or bad:
 *     the refusal series wears the deny token, the same one every `deny` badge in
 *     the product wears, so the dashboard and the explorer cannot disagree about
 *     what red means.
 *   - The one multi-series chart is the latency triple, and three series is where
 *     colour alone is comfortable for every reader. It is validated: worst
 *     all-pairs CVD ΔE 12.5 light / 13.9 dark under protanopia and deuteranopia,
 *     normal-vision worst pair 24.0 / 22.1 — both clear of the ≥ 8 / ≥ 15 gates.
 *     The light-mode amber sits below 3:1 against white, which obliges a relief
 *     channel; it has two, the direct end label and the data table.
 *
 * There is deliberately **no four-series stacked chart** on the dashboard. The
 * decision breakdown carries all four decisions, and it carries them as a table
 * with the kit's own decision badges — because `deny` and `reject` share one tone
 * by design in this product (`bn-badge`'s `decisionVariant`), and two adjacent
 * stacked segments in one tone are one segment to the eye.
 */

import { LitElement, css, html, nothing, svg, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import type { AnalyticsInterval, TimeseriesPoint } from "../api/analytics.js";

/** SeriesTone selects one of the `--series-*` slots. */
export type SeriesTone = "neutral" | "deny" | "warn" | "allow";

/** Detail of `bucket-click` — the half-open span of the bucket that was clicked,
 * ready to be handed to the explorer as `from` and `to`. */
export interface BucketClickDetail {
  from: string;
  to: string;
}

/** Detail of `item-click` on a ranked list. */
export interface RankItemClickDetail {
  value: string;
}

/**
 * breakable inserts a zero-width wrap opportunity after each dot in a dotted
 * identifier, so `warehouse.public.orders` wraps as `warehouse.` / `public.` /
 * `orders` in a narrow column instead of as `warehouse.public.ord` / `ers`.
 *
 * `<wbr>` is an element rather than a character, so the value is unchanged for
 * selection and copy — which matters, because a fully qualified table name copied
 * out of this list is pasted straight into a filter. It is built by splitting and
 * re-interpolating rather than with `unsafeHTML`: these values are customer data
 * and lit escapes every interpolated part.
 */
function breakable(value: string): TemplateResult {
  const parts = value.split(".");
  return html`${parts.map((part, i) =>
    i === 0 ? html`${part}` : html`<wbr />.${part}`,
  )}`;
}

/* ── geometry, shared by the two SVG charts ─────────────────────────────── */

const W = 720;
const H = 200;
const PAD_L = 56;
const PAD_R = 56; // room for a direct end label on the line chart
const PAD_T = 14;
const PAD_B = 28;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;
const BASE_Y = PAD_T + INNER_H;
/** Bars never fill their slot: `marks-and-anatomy` caps a column at 24px and
 * leaves the band's remainder as air. */
const BAR_MAX = 24;
/** The surface gap between adjacent bars. White does the separating; a stroke
 * around a mark would add ink that is not data. */
const BAR_GAP = 2;

/** INTERVAL_MS is each bucket width in milliseconds, so a clicked bar can name its
 * own half-open span without the caller doing the arithmetic. */
const INTERVAL_MS: Record<AnalyticsInterval, number> = {
  "5m": 5 * 60_000,
  "15m": 15 * 60_000,
  "1h": 3_600_000,
  "6h": 6 * 3_600_000,
  "1d": 24 * 3_600_000,
};

/** barPath is a column with a 4px rounded data-end and a square baseline. */
function barPath(x: number, y: number, w: number, h: number, r = 4): string {
  const rad = Math.min(r, w / 2, h);
  return (
    `M${x},${y + h} L${x},${y + rad} Q${x},${y} ${x + rad},${y} ` +
    `L${x + w - rad},${y} Q${x + w},${y} ${x + w},${y + rad} L${x + w},${y + h} Z`
  );
}

/**
 * tickAt decides whether bucket `i` gets an x-axis label.
 *
 * Roughly six labels across the axis, and the LAST bucket always gets one — a
 * reader needs to know where the axis ends. The `remaining` guard is what stops the
 * last two colliding: without it a modulo tick landing one bucket before the end
 * prints on top of the end label, which is how "07-24 07:00 07-24 12:00" happens.
 */
function tickAt(i: number, n: number, every: number): boolean {
  if (i === n - 1) return true;
  // A whole tick-gap of clearance, not half: a timestamp label is wide enough that
  // a modulo tick five buckets from the end still prints on top of the end label,
  // which is how "07-24 07:00 07-24 12:00" comes out as one smudge.
  return i % every === 0 && n - 1 - i >= every;
}

/** niceMax rounds an axis maximum up to a readable number, so the ticks read
 * 0 / 500 / 1,000 rather than 0 / 417 / 834. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * mag;
    if (candidate >= v) return candidate;
  }
  return 10 * mag;
}

/** bucketLabel formats a bucket start for the x-axis, at the grain the interval
 * implies. UTC always: every day boundary in this model is UTC and a local
 * reading would make a requested day and a returned bucket disagree by one. */
function bucketLabel(iso: string, interval: AnalyticsInterval): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const s = d.toISOString();
  return interval === "1d" ? s.slice(5, 10) : `${s.slice(5, 10)} ${s.slice(11, 16)}`;
}

/** bucketTitle is the fuller form used in tooltips and the data table. */
function bucketTitle(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

/** chartCss is the chrome both SVG charts share: recessive hairline grid, axis
 * text in a text token (never a series colour), the tooltip surface, and the
 * `<details>` data table that is the keyboard path. */
const chartCss = css`
  :host {
    display: block;
    position: relative;

    /* The series slots, declared HERE and not only on the page.
       A custom property a component only ever inherits is a component that
       renders black the first time it is mounted anywhere else — which is exactly
       what happened the first time these were rendered outside page-analytics.
       Every one is an alias of a --bino-* semantic token, so they follow the theme
       with no prefers-color-scheme query and there is no literal colour here.

       series-neutral is the one-hue magnitude slot every single-series chart and
       every inline bar uses. series-warn and series-deny are STATUS tones and are
       used only where the series genuinely means a fault or a refusal, never as
       "series 4". Validated with the dataviz palette checker in both modes: worst
       all-pairs CVD delta-E 12.5 light / 13.9 dark, normal-vision worst pair
       24.0 / 22.1 — clear of the 8 and 15 gates. Light-mode amber sits below 3:1
       on white, which obliges a relief channel; it has two, the direct end label
       and the data table. */
    --series-neutral: var(--bino-primary);
    --series-warn: var(--bino-warning);
    --series-deny: var(--bino-decision-deny);
    --series-allow: var(--bino-decision-allow);
    --series-track: var(--bino-surface-inset);
  }
  :host([dimmed]) .plot {
    /* A refetch holds the previous render at reduced opacity: no skeleton flash,
       no layout jump. */
    opacity: 0.45;
    transition: opacity var(--bino-transition-fast);
  }
  svg {
    display: block;
    width: 100%;
    height: auto;
    touch-action: none;
  }
  .grid {
    stroke: var(--bino-border);
    stroke-width: 1;
    fill: none;
  }
  .axis-text {
    fill: var(--bino-text-secondary);
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-sm);
    font-variant-numeric: tabular-nums;
  }
  .end-label {
    fill: var(--bino-text-muted);
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-sm);
    font-weight: 600;
  }
  .hit {
    fill: transparent;
  }
  .hit.clickable {
    cursor: pointer;
  }
  .tone-neutral {
    fill: var(--series-neutral);
    stroke: var(--series-neutral);
  }
  .tone-deny {
    fill: var(--series-deny);
    stroke: var(--series-deny);
  }
  .tone-warn {
    fill: var(--series-warn);
    stroke: var(--series-warn);
  }
  .tone-allow {
    fill: var(--series-allow);
    stroke: var(--series-allow);
  }
  .bar {
    stroke: none;
  }
  .bar.hot {
    /* The hovered mark responds without changing hue: identity must not move. */
    filter: brightness(1.15);
  }
  .line {
    fill: none;
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
  }
  .dot {
    /* The 2px surface ring keeps an end marker legible where it crosses a line. */
    stroke: var(--bino-surface);
    stroke-width: 2;
  }
  .crosshair {
    stroke: var(--bino-border-hover);
    stroke-width: 1;
  }
  .incomplete {
    fill: var(--bino-surface-inset);
  }
  .incomplete-hatch {
    stroke: var(--bino-border-hover);
    stroke-width: 1;
  }
  .incomplete-label {
    fill: var(--bino-text-secondary);
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-xs);
  }
  .tip {
    position: absolute;
    z-index: 1;
    transform: translate(-50%, calc(-100% - var(--bino-space-xs)));
    pointer-events: none;
    white-space: nowrap;
    background: var(--bino-surface);
    border: 1px solid var(--bino-border);
    border-radius: var(--bino-radius-sm);
    box-shadow: var(--bino-shadow-dropdown);
    padding: var(--bino-space-xs) var(--bino-space-sm);
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text);
  }
  .tip .when {
    display: block;
    color: var(--bino-text-secondary);
    margin-bottom: var(--bino-space-xs);
  }
  .tip-row {
    display: flex;
    align-items: baseline;
    gap: var(--bino-space-xs);
  }
  .tip-key {
    display: inline-block;
    width: 0.75rem;
    height: 0.125rem;
    border-radius: var(--bino-radius-pill);
  }
  .tip-value {
    font-family: var(--bino-font-mono);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .tip-name {
    color: var(--bino-text-secondary);
  }
  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--bino-space-md);
    margin-top: var(--bino-space-xs);
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
  }
  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: var(--bino-space-xs);
  }
  .legend-key {
    width: 0.875rem;
    height: 0.1875rem;
    border-radius: var(--bino-radius-pill);
  }
  .key-neutral {
    background: var(--series-neutral);
  }
  .key-deny {
    background: var(--series-deny);
  }
  .key-warn {
    background: var(--series-warn);
  }
  .key-allow {
    background: var(--series-allow);
  }
  .empty {
    padding: var(--bino-space-xl) var(--bino-space-md);
    text-align: center;
    color: var(--bino-text-secondary);
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-base);
  }
  details {
    margin-top: var(--bino-space-sm);
  }
  summary {
    cursor: pointer;
    width: fit-content;
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
  }
  summary:focus-visible {
    outline: none;
    border-radius: var(--bino-radius-sm);
    box-shadow: var(--bino-focus-ring-shadow);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: var(--bino-space-sm);
    font-size: var(--bino-font-size-sm);
  }
  th,
  td {
    text-align: left;
    padding: var(--bino-space-xs) var(--bino-space-sm);
    border-bottom: 1px solid var(--bino-border);
  }
  th {
    color: var(--bino-text-secondary);
    font-weight: 600;
  }
  td {
    font-family: var(--bino-font-mono);
    font-variant-numeric: tabular-nums;
    color: var(--bino-text);
  }
  td.when {
    color: var(--bino-text-secondary);
  }
  .drill {
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-sm);
    background: none;
    border: 1px solid var(--bino-border-light);
    border-radius: var(--bino-radius-sm);
    padding: 0 var(--bino-space-sm);
    color: var(--bino-primary);
    cursor: pointer;
  }
  .drill:hover {
    background: var(--bino-surface-hover);
  }
  .drill:focus-visible {
    outline: none;
    box-shadow: var(--bino-focus-ring-shadow);
  }
`;

/* ── an-columns ─────────────────────────────────────────────────────────── */

/**
 * `<an-columns>` is a single-series column chart over time buckets.
 *
 * One series, so there is no legend — a box with one swatch restates the card
 * title. Identity comes from the title; magnitude from bar length.
 *
 * Two behaviours are obligations rather than features:
 *
 *   - **The region beyond the ingest watermark is marked**, not drawn as zero
 *     (`SC-S806`). It is hatched *and* labelled, so the mark is never
 *     texture-only.
 *   - **Every value is reachable without hovering.** The SVG is `role="img"` with
 *     a computed summary, and the `<details>` table below is the keyboard and
 *     screen-reader path — including the drill-down, which is a button per row
 *     there rather than a click target only a mouse can reach.
 */
@customElement("an-columns")
export class AnColumns extends LitElement {
  /** `[timestamp, value]` pairs, zero-filled by the API across the whole range. */
  @property({ attribute: false }) points: TimeseriesPoint[] = [];
  /** What is plotted, for the accessible summary and the table header. */
  @property() label = "";
  /** The bucket width the server actually used — not the one that was asked for. */
  @property() interval: AnalyticsInterval = "1h";
  @property() tone: SeriesTone = "neutral";
  /** The ingest watermark. Buckets at or after it are incomplete. */
  @property() asOf: string | null = null;
  /** Emit `bucket-click` from a bar and offer a drill button in the table. */
  @property({ type: Boolean }) drillable = false;
  /** Hold the previous render at reduced opacity during a refetch. */
  @property({ type: Boolean, reflect: true }) dimmed = false;

  @state() private hover?: { i: number; px: number; py: number };

  @query("svg") private svgEl?: SVGSVGElement;

  static override styles = chartCss;

  private slotW(): number {
    return INNER_W / Math.max(1, this.points.length);
  }

  private onMove(e: PointerEvent) {
    const el = this.svgEl;
    const n = this.points.length;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const scale = rect.width / W;
    const i = Math.floor(((e.clientX - rect.left) / scale - PAD_L) / this.slotW());
    if (i < 0 || i >= n) {
      this.hover = undefined;
      return;
    }
    this.hover = { i, px: e.clientX - rect.left, py: e.clientY - rect.top };
  }

  private span(i: number): BucketClickDetail {
    const at = this.points[i]?.[0] ?? "";
    const start = new Date(at).getTime();
    const width = INTERVAL_MS[this.interval] ?? INTERVAL_MS["1h"];
    return { from: new Date(start).toISOString(), to: new Date(start + width).toISOString() };
  }

  private drill(i: number) {
    this.dispatchEvent(
      new CustomEvent<BucketClickDetail>("bucket-click", {
        detail: this.span(i),
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const pts = this.points;
    const n = pts.length;
    if (n === 0) {
      return html`<div class="empty" role="img" aria-label=${`${this.label}: no data`}>
        No data for this window.
      </div>`;
    }
    const values = pts.map((p) => p[1]);
    const peak = Math.max(...values);
    const total = values.reduce((a, b) => a + b, 0);
    const top = niceMax(peak);
    const slot = this.slotW();
    const barW = Math.max(1, Math.min(slot - BAR_GAP, BAR_MAX));
    const y = (v: number) => PAD_T + INNER_H * (1 - v / top);

    const grid = [0, 0.5, 1].map(
      (f) =>
        svg`<line class="grid" x1=${PAD_L} y1=${PAD_T + INNER_H * (1 - f)} x2=${W - PAD_R} y2=${PAD_T + INNER_H * (1 - f)} />`,
    );
    const yTicks = [0, 0.5, 1].map(
      (f) =>
        svg`<text class="axis-text" x=${PAD_L - 8} y=${PAD_T + INNER_H * (1 - f)}
              text-anchor="end" dominant-baseline="central">${(f * top).toLocaleString()}</text>`,
    );
    const every = Math.max(1, Math.ceil(n / 6));
    const xTicks = pts.map((p, i) =>
      tickAt(i, n, every)
        ? svg`<text class="axis-text" x=${PAD_L + i * slot + slot / 2} y=${BASE_Y + 16}
                text-anchor=${i === n - 1 ? "end" : "middle"}>${bucketLabel(p[0], this.interval)}</text>`
        : nothing,
    );

    // The incomplete region. `asOf` is a domain timestamp, so the boundary is the
    // first bucket whose START is at or after it — a bucket the watermark falls
    // inside is only partly ingested and is therefore incomplete too.
    let firstIncomplete = -1;
    if (this.asOf) {
      const cut = new Date(this.asOf).getTime();
      const width = INTERVAL_MS[this.interval] ?? INTERVAL_MS["1h"];
      firstIncomplete = pts.findIndex((p) => new Date(p[0]).getTime() + width > cut);
    }
    const incomplete =
      firstIncomplete >= 0 && firstIncomplete < n
        ? svg`
          <defs>
            <pattern id="incomplete" width="6" height="6" patternUnits="userSpaceOnUse"
                     patternTransform="rotate(45)">
              <line class="incomplete-hatch" x1="0" y1="0" x2="0" y2="6" />
            </pattern>
          </defs>
          <rect x=${PAD_L + firstIncomplete * slot} y=${PAD_T}
                width=${Math.max(0, W - PAD_R - (PAD_L + firstIncomplete * slot))} height=${INNER_H}
                fill="url(#incomplete)" />
          <text class="incomplete-label" x=${W - PAD_R - 4} y=${PAD_T + 10} text-anchor="end">
            not yet ingested
          </text>`
        : nothing;

    const bars = pts.map((p, i) => {
      if (p[1] <= 0) return nothing;
      const bx = PAD_L + i * slot + (slot - barW) / 2;
      const by = y(p[1]);
      return svg`<path class=${`bar tone-${this.tone} ${this.hover?.i === i ? "hot" : ""}`}
                       d=${barPath(bx, by, barW, Math.max(1, BASE_Y - by))} />`;
    });
    // One hit rect per slot, so the pointer only has to be in the column — an
    // 8px-wide bar is a pinpoint nobody lands on.
    const hits = pts.map(
      (_, i) =>
        svg`<rect class=${`hit ${this.drillable ? "clickable" : ""}`} x=${PAD_L + i * slot} y=${PAD_T}
                  width=${slot} height=${INNER_H}
                  @click=${() => this.drillable && this.drill(i)} />`,
    );

    const peakAt = bucketTitle(pts[values.indexOf(peak)]?.[0] ?? "");
    const summary =
      `${this.label}: ${total.toLocaleString()} in total over ${n} ${this.interval} buckets, ` +
      `peak ${peak.toLocaleString()} at ${peakAt}.` +
      (firstIncomplete >= 0
        ? ` The last ${n - firstIncomplete} bucket(s) are beyond the ingest watermark and are incomplete.`
        : "");

    const h = this.hover;
    return html`
      <div class="plot">
        <svg
          viewBox="0 0 ${W} ${H}"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label=${summary}
          @pointermove=${this.onMove}
          @pointerleave=${() => (this.hover = undefined)}
        >
          ${incomplete}${grid}${yTicks}${xTicks}${bars}${hits}
        </svg>
        ${h
          ? html`<div class="tip" style=${`left:${h.px}px;top:${h.py}px`}>
              <span class="when">${bucketTitle(pts[h.i]?.[0] ?? "")}</span>
              <span class="tip-row">
                <span class=${`tip-key key-${this.tone}`}></span>
                <span class="tip-value">${(pts[h.i]?.[1] ?? 0).toLocaleString()}</span>
                <span class="tip-name">${this.label}</span>
              </span>
            </div>`
          : nothing}
      </div>
      <details>
        <summary>Data table — ${this.label}</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Bucket start (UTC)</th>
              <th scope="col">${this.label}</th>
              <th scope="col">Ingested</th>
              ${this.drillable ? html`<th scope="col">Records</th>` : nothing}
            </tr>
          </thead>
          <tbody>
            ${pts.map(
              (p, i) => html`<tr>
                <td class="when">${bucketTitle(p[0])}</td>
                <td>${p[1].toLocaleString()}</td>
                <td class="when">
                  ${firstIncomplete >= 0 && i >= firstIncomplete ? "incomplete" : "complete"}
                </td>
                ${this.drillable
                  ? html`<td>
                      <button class="drill" type="button" @click=${() => this.drill(i)}>
                        Open in explorer
                      </button>
                    </td>`
                  : nothing}
              </tr>`,
            )}
          </tbody>
        </table>
      </details>
    `;
  }
}

/* ── an-lines ───────────────────────────────────────────────────────────── */

/** LineSeries is one plotted line. `label` is what the legend and the direct end
 * label read; `tone` is its `--series-*` slot. */
export interface LineSeries {
  label: string;
  tone: SeriesTone;
  points: TimeseriesPoint[];
}

/**
 * `<an-lines>` plots up to three series over the same buckets — the latency
 * triple (`04` §6.2 A2).
 *
 * A legend is always present, because two or more series must never depend on
 * colour-matching alone, and the end of each line is directly labelled when the
 * three separate far enough at the right edge. When they converge the labels are
 * **dropped rather than nudged apart**: a label pushed off its line detaches from
 * it and reads as noise, and the legend and tooltip still carry the identity.
 *
 * The crosshair finds the X and the tooltip lists **every** series at that bucket,
 * so the pointer never has to land on a 2px stroke.
 */
@customElement("an-lines")
export class AnLines extends LitElement {
  @property({ attribute: false }) series: LineSeries[] = [];
  @property() label = "";
  @property() interval: AnalyticsInterval = "1h";
  @property() asOf: string | null = null;
  /** Unit suffix for the axis and the tooltip, e.g. `ms`. */
  @property() unit = "";
  @property({ type: Boolean, reflect: true }) dimmed = false;

  @state() private hover?: { i: number; px: number; py: number };

  @query("svg") private svgEl?: SVGSVGElement;

  static override styles = chartCss;

  private buckets(): string[] {
    return this.series[0]?.points.map((p) => p[0]) ?? [];
  }

  private slotW(): number {
    return INNER_W / Math.max(1, this.buckets().length);
  }

  private onMove(e: PointerEvent) {
    const el = this.svgEl;
    const n = this.buckets().length;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const scale = rect.width / W;
    const i = Math.round(((e.clientX - rect.left) / scale - PAD_L - this.slotW() / 2) / this.slotW());
    if (i < 0 || i >= n) {
      this.hover = undefined;
      return;
    }
    this.hover = { i, px: e.clientX - rect.left, py: e.clientY - rect.top };
  }

  override render() {
    const buckets = this.buckets();
    const n = buckets.length;
    const all = this.series.flatMap((s) => s.points.map((p) => p[1]));
    if (n === 0 || all.length === 0) {
      return html`<div class="empty" role="img" aria-label=${`${this.label}: no data`}>
        No data for this window.
      </div>`;
    }
    const top = niceMax(Math.max(...all));
    const slot = this.slotW();
    const cx = (i: number) => PAD_L + i * slot + slot / 2;
    const cy = (v: number) => PAD_T + INNER_H * (1 - v / top);

    const grid = [0, 0.5, 1].map(
      (f) =>
        svg`<line class="grid" x1=${PAD_L} y1=${PAD_T + INNER_H * (1 - f)} x2=${W - PAD_R} y2=${PAD_T + INNER_H * (1 - f)} />`,
    );
    const yTicks = [0, 0.5, 1].map(
      (f) =>
        svg`<text class="axis-text" x=${PAD_L - 8} y=${PAD_T + INNER_H * (1 - f)}
              text-anchor="end" dominant-baseline="central">${Math.round(f * top).toLocaleString()}</text>`,
    );
    const every = Math.max(1, Math.ceil(n / 6));
    const xTicks = buckets.map((b, i) =>
      tickAt(i, n, every)
        ? svg`<text class="axis-text" x=${cx(i)} y=${BASE_Y + 16}
                text-anchor=${i === n - 1 ? "end" : "middle"}>${bucketLabel(b, this.interval)}</text>`
        : nothing,
    );

    let firstIncomplete = -1;
    if (this.asOf) {
      const cut = new Date(this.asOf).getTime();
      const width = INTERVAL_MS[this.interval] ?? INTERVAL_MS["1h"];
      firstIncomplete = buckets.findIndex((b) => new Date(b).getTime() + width > cut);
    }
    const incomplete =
      firstIncomplete >= 0 && firstIncomplete < n
        ? svg`
          <defs>
            <pattern id="incomplete" width="6" height="6" patternUnits="userSpaceOnUse"
                     patternTransform="rotate(45)">
              <line class="incomplete-hatch" x1="0" y1="0" x2="0" y2="6" />
            </pattern>
          </defs>
          <rect x=${cx(firstIncomplete) - slot / 2} y=${PAD_T}
                width=${Math.max(0, W - PAD_R - (cx(firstIncomplete) - slot / 2))} height=${INNER_H}
                fill="url(#incomplete)" />
          <text class="incomplete-label" x=${W - PAD_R - 4} y=${PAD_T + 10} text-anchor="end">
            not yet ingested
          </text>`
        : nothing;

    const paths = this.series.map(
      (s) =>
        svg`<path class=${`line tone-${s.tone}`}
                  d=${s.points.map((p, i) => `${i === 0 ? "M" : "L"}${cx(i)},${cy(p[1])}`).join(" ")} />`,
    );
    const dots = this.series.map((s) => {
      const last = s.points[s.points.length - 1];
      return last ? svg`<circle class=${`dot tone-${s.tone}`} cx=${cx(n - 1)} cy=${cy(last[1])} r="4" />` : nothing;
    });

    // Direct end labels, only when the three ends separate. 16 viewBox units is a
    // comfortable line of the label's own type; below it two labels stop reading as
    // two, and nudging them apart would detach them from their lines.
    const ends = this.series
      .map((s) => ({ s, y: cy(s.points[s.points.length - 1]?.[1] ?? 0) }))
      .sort((a, b) => a.y - b.y);
    const separated = ends.every((e, i) => i === 0 || Math.abs(e.y - (ends[i - 1]?.y ?? 0)) >= 16);
    const endLabels = separated
      ? ends.map(
          (e) =>
            svg`<text class="end-label" x=${W - PAD_R + 8} y=${e.y} dominant-baseline="central">${e.s.label}</text>`,
        )
      : nothing;

    const h = this.hover;
    const crosshair = h
      ? svg`<line class="crosshair" x1=${cx(h.i)} y1=${PAD_T} x2=${cx(h.i)} y2=${BASE_Y} />`
      : nothing;

    const summary =
      `${this.label}: ${this.series
        .map((s) => {
          const vals = s.points.map((p) => p[1]);
          return `${s.label} peaks at ${Math.max(...vals).toLocaleString()}${this.unit}`;
        })
        .join(", ")} over ${n} ${this.interval} buckets.` +
      (firstIncomplete >= 0 ? " The trailing buckets are beyond the ingest watermark." : "");

    return html`
      <div class="plot">
        <svg
          viewBox="0 0 ${W} ${H}"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label=${summary}
          @pointermove=${this.onMove}
          @pointerleave=${() => (this.hover = undefined)}
        >
          ${incomplete}${grid}${yTicks}${xTicks}${crosshair}${paths}${dots}${endLabels}
        </svg>
        ${h
          ? html`<div class="tip" style=${`left:${h.px}px;top:${h.py}px`}>
              <span class="when">${bucketTitle(buckets[h.i] ?? "")}</span>
              ${this.series.map(
                (s) => html`<span class="tip-row">
                  <span class=${`tip-key key-${s.tone}`}></span>
                  <span class="tip-value"
                    >${(s.points[h.i]?.[1] ?? 0).toLocaleString()}${this.unit}</span
                  >
                  <span class="tip-name">${s.label}</span>
                </span>`,
              )}
            </div>`
          : nothing}
      </div>
      <div class="legend" role="list">
        ${this.series.map(
          (s) => html`<span class="legend-item" role="listitem">
            <span class=${`legend-key key-${s.tone}`}></span>${s.label}
          </span>`,
        )}
      </div>
      <details>
        <summary>Data table — ${this.label}</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Bucket start (UTC)</th>
              ${this.series.map((s) => html`<th scope="col">${s.label}</th>`)}
            </tr>
          </thead>
          <tbody>
            ${buckets.map(
              (b, i) => html`<tr>
                <td class="when">${bucketTitle(b)}</td>
                ${this.series.map(
                  (s) => html`<td>${(s.points[i]?.[1] ?? 0).toLocaleString()}${this.unit}</td>`,
                )}
              </tr>`,
            )}
          </tbody>
        </table>
      </details>
    `;
  }
}

/* ── an-ranks ───────────────────────────────────────────────────────────── */

/** RankRow is one entry of a ranked list. `denied` is optional and is rendered as
 * a second number, never as a second colour on the same bar. */
export interface RankRow {
  /** The drill key — what `item-click` carries and what the explorer filters on. */
  value: string;
  count: number;
  denied?: number;
  /**
   * Content that **replaces** the printed value as the row's label — a decision
   * badge, say. It replaces rather than precedes it because a badge whose text is
   * the value renders "allow allow", and the badge is already the better label:
   * it carries the product's own tone for that value.
   */
  badge?: TemplateResult;
}

/**
 * `<an-ranks>` is the ranked top-N: a table with an inline bar cell (`04` §6.2's
 * own form for A4 and A5, and visually the ranked bars A3 and A6 ask for).
 *
 * It is a table rather than an SVG chart on purpose. The categories are nominal —
 * table names, subject ids, error codes — so one hue for every bar is correct, and
 * a real `<table>` is keyboard-navigable and screen-reader-complete by
 * construction with no `role="img"` summary to maintain. The bar is decoration
 * over a number that is always printed.
 */
@customElement("an-ranks")
export class AnRanks extends LitElement {
  @property({ attribute: false }) rows: RankRow[] = [];
  /** Header for the key column. */
  @property() keyLabel = "";
  /** Header for the value column. */
  @property() valueLabel = "Records";
  @property() tone: SeriesTone = "neutral";
  /** Show the `denied` column. */
  @property({ type: Boolean }) showDenied = false;
  /** Make each key a button that emits `item-click`. */
  @property({ type: Boolean }) drillable = false;
  @property({ type: Boolean, reflect: true }) dimmed = false;

  static override styles = [
    chartCss,
    css`
      :host([dimmed]) table {
        opacity: 0.45;
      }
      table {
        margin-top: 0;
      }
      th.key {
        font-family: var(--bino-font-mono);
        color: var(--bino-text);
        font-weight: 400;
        /* A fully qualified table name is long and must wrap, but it must wrap at
           a dot rather than mid-token: overflow-wrap "anywhere" splits "customers"
           across two lines, which makes a name unreadable in the one column where
           a reader is comparing names character by character. */
        overflow-wrap: break-word;
        word-break: normal;
        min-width: 10rem;
      }
      thead th.key {
        font-family: var(--bino-font-sans);
        color: var(--bino-text-secondary);
        font-weight: 600;
      }
      td.bar-cell {
        width: 30%;
        min-width: 4rem;
      }
      .bar-head {
        /* The bar is decoration over a number that is always printed, so its
           column header exists for the screen reader's benefit and not the eye's. */
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }
      .track {
        position: relative;
        height: 0.5rem;
        border-radius: var(--bino-radius-pill);
        background: var(--series-track);
        overflow: hidden;
      }
      .fill {
        height: 100%;
        border-radius: var(--bino-radius-pill);
      }
      .fill-neutral {
        background: var(--series-neutral);
      }
      .fill-deny {
        background: var(--series-deny);
      }
      .fill-warn {
        background: var(--series-warn);
      }
      .fill-allow {
        background: var(--series-allow);
      }
      .key-button {
        font-family: var(--bino-font-mono);
        font-size: var(--bino-font-size-base);
        background: none;
        border: none;
        border-radius: var(--bino-radius-sm);
        padding: 0;
        color: var(--bino-primary);
        cursor: pointer;
        text-align: left;
        overflow-wrap: anywhere;
      }
      .key-button:hover {
        text-decoration: underline;
      }
      .key-button:focus-visible {
        outline: none;
        box-shadow: var(--bino-focus-ring-shadow);
      }
      td.num {
        text-align: right;
        white-space: nowrap;
      }
    `,
  ];

  private pick(value: string) {
    this.dispatchEvent(
      new CustomEvent<RankItemClickDetail>("item-click", {
        detail: { value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (this.rows.length === 0) {
      return html`<div class="empty">Nothing in this window.</div>`;
    }
    const top = Math.max(1, ...this.rows.map((r) => r.count));
    return html`<table>
      <thead>
        <tr>
          <th scope="col" class="key">${this.keyLabel}</th>
          <th scope="col"><span class="bar-head">Share</span></th>
          <th scope="col" class="num">${this.valueLabel}</th>
          ${this.showDenied ? html`<th scope="col" class="num">Denied</th>` : nothing}
        </tr>
      </thead>
      <tbody>
        ${this.rows.map(
          (r) => html`<tr>
            <th scope="row" class="key" title=${r.value}>
              ${this.drillable
                ? html`<button
                    class="key-button"
                    type="button"
                    aria-label=${r.value}
                    @click=${() => this.pick(r.value)}
                  >
                    ${r.badge ?? breakable(r.value)}
                  </button>`
                : (r.badge ?? breakable(r.value))}
            </th>
            <td class="bar-cell">
              <div class="track" aria-hidden="true">
                <div
                  class=${`fill fill-${this.tone}`}
                  style=${`width:${Math.max(2, (r.count / top) * 100)}%`}
                ></div>
              </div>
            </td>
            <td class="num">${r.count.toLocaleString()}</td>
            ${this.showDenied
              ? html`<td class="num">${(r.denied ?? 0).toLocaleString()}</td>`
              : nothing}
          </tr>`,
        )}
      </tbody>
    </table>`;
  }
}

/* ── an-stat ────────────────────────────────────────────────────────────── */

/**
 * `<an-stat>` is one stat tile. A single number is not a one-bar bar chart, and
 * the tile's contract is fixed: a sentence-case label, the value in the product's
 * own sans with **proportional** figures (tabular digits make `121` look loose at
 * this size), and a `window` line — because `SC-F808` requires every tile to state
 * the window it is over.
 */
@customElement("an-stat")
export class AnStat extends LitElement {
  @property() label = "";
  @property() value = "—";
  /** The window this number is over. Required by the dashboard's own contract. */
  @property() window = "";
  /** A caveat: “approximate”, “from the daily rollup”, “incomplete window”. */
  @property() note = "";
  @property() tone: SeriesTone | "" = "";
  @property({ type: Boolean, reflect: true }) dimmed = false;

  static override styles = css`
    :host {
      display: block;
      padding: var(--bino-space-md);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      font-family: var(--bino-font-sans);

      /* Declared here for the same reason chartCss declares them: a tile must not
         depend on its host having defined the slot it paints with. */
      --series-neutral: var(--bino-primary);
      --series-warn: var(--bino-warning);
      --series-deny: var(--bino-decision-deny);
      --series-allow: var(--bino-decision-allow);
    }
    :host([dimmed]) {
      opacity: 0.55;
    }
    .label {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .value {
      display: block;
      margin-top: var(--bino-space-xs);
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      line-height: 1.1;
      color: var(--bino-text);
      /* A headline number never wraps: "241 / ms" on two lines reads as two facts.
         Proportional figures deliberately — tabular digits make 121 look loose at
         this size, and nothing here has to align vertically with anything. */
      white-space: nowrap;
    }
    .value.deny {
      color: var(--series-deny);
    }
    .value.warn {
      color: var(--series-warn);
    }
    .value.allow {
      color: var(--series-allow);
    }
    .value.neutral {
      color: var(--bino-text);
    }
    .window,
    .note {
      display: block;
      margin-top: var(--bino-space-xs);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
    }
    .note {
      color: var(--bino-warning-text);
    }
  `;

  override render() {
    return html`
      <span class="label">${this.label}</span>
      <span class=${`value ${this.tone}`}>${this.value}</span>
      ${this.window ? html`<span class="window">${this.window}</span>` : nothing}
      ${this.note ? html`<span class="note">${this.note}</span>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "an-columns": AnColumns;
    "an-lines": AnLines;
    "an-ranks": AnRanks;
    "an-stat": AnStat;
  }
}
